import {Component, ViewChild} from '@angular/core';
import {NgForm} from '@angular/forms';
import {ReplaySubject} from 'rxjs';
import {AddressModel} from '../../../../components/forms/controls/addresspicker/addresspicker.component';
import {AddressMapper} from '../../../../domain/model/common/mappers/address-mapper.class';
import {StepScreenModel} from '../../../shared/step/step-screen.component';
import {FormErrors} from '../../../../components/forms/validation/form-validator.class';
import {MedicalEducation} from '../../../../domain/model/education/medical/medical-education.model';
import {CountryService} from '../../../../domain/model/info/country/country.service';
import {MedicalEducationService} from '../../../../domain/model/education/medical/medical-education.service';
import {FileLocationFactory} from '../../../../components/files/service/file-location.class';
import {Model} from '../../../../domain/model/common/model.class';
import {MonthPickerComponentModel} from '../../../../components/forms/controls/monthpicker/month-picker.component';
import {PlaceInfo} from '../../../../domain/model/info/place/place-info.model';
import {PlaceInfoService} from '../../../../domain/model/info/place/place-info.service';
import {State} from '../../../../domain/model/info/country/state.model';
import moment = require('moment');
import ProjectionInformation = Model.ProjectionInformation;
import {VerificationService} from '../../../../components/verification/service/verification.service';

//region Other Classes
class UserChange<T> {
    constructor(readonly value: T) {}
}
//endregion

//region Country Change
const Countries = {USA: 'United States'};
//endregion

@Component({
    template: require('./medical-education-screen.component.html'),
    styles: [require('./medical-education-screen.component.scss')]
})
export class MedicalEducationScreenComponent {

    constructor(readonly educationService: MedicalEducationService,
                countryService: CountryService,
                placeInfoService: PlaceInfoService,
                readonly fileLocationFactory: FileLocationFactory,
                readonly verificationService: VerificationService) {
        countryService.getCountriesProjection().subscribe(countries => this.countries = countries.map(c => c.name));
        countryService.getStates().subscribe(states => {
            this.states = states;
            this.stateValues = states.map(s => s.name);
        });

        placeInfoService.getPlaces().subscribe(schools => {
            this.schools = schools;
            this.schoolValues = schools.map(s => s.name);
        });

        this.stepScreenModel.dataModel$.subscribe(() => this.handleAttendedDateChange());
    }

    // region Form
    readonly form$ = new ReplaySubject<NgForm>(1);

    @ViewChild(NgForm) set form(value: NgForm) {
        this.form$.next(value);
    }

    // endregion

    private readonly _addressMapper = new AddressMapper();

    countries: Array<string> = [];
    states: Array<State> = [];
    stateValues: Array<string> = [];
    schools: Array<PlaceInfo> = [];
    schoolValues: Array<string> = [];
    phoneMask = ['(', /\d/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/];
    graduationDateMonthPickerModel = new MonthPickerComponentModel({
        minDate: moment().subtract(100, 'year'),
        maxDate: moment().add(5, 'year')
    });
    attendanceDateConstrains = {
        minDate: moment().subtract(100, 'year'),
        maxDate: moment().add(5, 'year')
    };
    attendanceFromMonthPickerModel = new MonthPickerComponentModel(this.attendanceDateConstrains);
    attendanceToMonthPickerModel = new MonthPickerComponentModel(this.attendanceDateConstrains);
    proofDocumentFileLocation = this.fileLocationFactory.create('medicalEducation', 'currentUser', new ProjectionInformation('proofDocument'));
    showAttendanceExplanation = false;

    degreeAwardedOptions = ['', 'MD', 'DO', 'NP', 'PA'];

    stepScreenModel = new StepScreenModel<MedicalEducation>({
        initialDataModel: new MedicalEducation(),
        specificValidationMessages: {},
        _loadDataFn: () => {
            this.verificationService.setUrl('medicalEducation');
            return this.educationService.getCurrentUserModel();
        },
        _submitDataFn: (model: MedicalEducation) => {
            return this.educationService.updateCurrentUserModel(model);
        }
    });

    get model(): MedicalEducation {
        return this.stepScreenModel.dataModel;
    }

    get formErrors(): FormErrors {
        return this.stepScreenModel.formErrors;
    }

    handleAddressUpdated(addressModel: AddressModel) {
        this._addressMapper.update(this.model.address, addressModel);
    }

    handleAttendedDateChange() {
        this.showAttendanceExplanation = (this.model.attendance.duration && this.model.attendance.duration.to && this.model.attendance.duration.from)
            ? Math.round(this.model.attendance.duration.to.diff(this.model.attendance.duration.from, 'years', true)) !== 4
            : false;
    }

    get medicalSchool(): string | undefined { return this.model.institution; }

    set medicalSchool(name: string | undefined) {
        this.model.institution = name;

        const school = this.schools.find(s => s.name === name);
        if (school) {
            this.model.address.address1 = school.address1;
            this.model.address.postalCode = school.postalCode;
            this.model.address.city = school.city;
            this.model.address.stateOrProvince = this._getStateName(school.stateOrProvince);
            this.changeCountry(school.country);
            this.model.contact.phone = school.phone;
            this.model.contact.fax = school.fax;
        }
    }

    private _getStateName(code: string): string | undefined {
        const state = this.states.find(s => s.code === code);
        if (state) return state.name;
    }

    countryChangeMessage: string = '';

    private _firstSelectedCountryLazy?: string;

    private get _firstSelectedCountry(): string | undefined {
        if (this._firstSelectedCountryLazy === undefined) this._firstSelectedCountryLazy = this.model.address.country;
        return this._firstSelectedCountryLazy;
    }

    changeCountry(newCountry: string) {
        if (newCountry === Countries.USA && this._firstSelectedCountry !== Countries.USA) {
            this.countryChangeMessage = 'Since you selected US for your med school, the ECFMG will be deleted and please update the pre-med education.';
        }
        else if (newCountry !== Countries.USA && this._firstSelectedCountry === Countries.USA) {
            this.countryChangeMessage = 'Since you selected a country other than US for your med school, please update the ECFMG information as well by clicking back after saving this form.';
        }
        else this.countryChangeMessage = '';
        this.model.address.country = newCountry;
    }
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/@angularclass/hmr-loader!./~/tslint-loader!./src/app/pages/education/steps/medical/medical-education-screen.component.ts